// @flow

import crypto from 'crypto';
import {Tag, ContentGroupItem} from 'docs/util';

import type {MarkdocTagRegistration} from 'docs/markdoc';
import type {Node as MarkdocAstNode} from '@markdoc/markdoc';

const generateCodeTabID = (node: MarkdocAstNode) => {
  // NOTE (matv): It would be beneficial if every Markdoc node had a
  // stable ID generated that did not depend directly on the content itself.
  // This could potentially beneficial for editing purposes, translation,
  // and more. This code tab ID is used for code copying analytics and
  // rendering a copy button.

  // We do this on a AST node instead of a render tag or in React,
  // because we should be able to compute IDs without needing to render the
  // content.
  const rawContent = node.children
    .map((child) =>
      child.children ? child.children[0]?.attributes?.content : '',
    )
    .join('');

  // This hash function is the fastest one in Node. It takes < 5 ms.
  const hash = crypto
    .createHash('sha1')
    .update(node.lines[0] + rawContent)
    .digest('hex');

  return `codetabs_${hash}`;
};

export const CodeTabGroup: MarkdocTagRegistration = {
  tag: 'code-tabs',
  component: 'CodeTabGroupMarkdocShim',
  attributes: {
    pref: {
      type: String,
      required: false,
      errorLevel: 'critical',
      default: 'lang',
      matches: [
        'html-or-react',
        'lang-android',
        'lang-ios',
        'lang-js',
        'lang',
        'web-tech',
        'html-or-css',
        'react-hooks',
      ],
      description:
        "Set this store the user's selection in a pref (a cookie). This allows syncing across code groups.",
    },
  },
  render(node, config) {
    const codeTabGroupID = generateCodeTabID(node);

    const attributes = node.renderAttributes(this.attributes, config);
    const children = node.renderChildren(config);

    const items = ContentGroupItem.fromMarkdocTags(children);
    return new Tag(this.tag, {...attributes, items, codeTabGroupID});
  },
};
